import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_PRICING_PLANS_RESOURCE } from 'src/features/pricingPlan/api/useListPricingPlans';
import PricingPlanFormModal from 'src/features/pricingPlan/components/form/PricingPlanFormModal';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { useCreatePricingPlanVersion } from 'src/features/pricingPlan/api/useCreatePricingPlanVersion';
import { useParams } from 'react-router-dom';

interface UpdatePricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan: PricingPlan;
    children?: React.ReactNode;
}

function CopyPricingPlanVersionFormModal<TData>({
    className,
    semantics,
    children,
    selectedPricingPlan,
}: UpdatePricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const { planId = '' } = useParams();

    const { mutate, isPending } = useCreatePricingPlanVersion({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.copyPricingPlanVersionSuccess),
                description: formatMessage(pricingPlanMessages.copyPricingPlanVersionSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <PricingPlanFormModal
            selectedPricingPlan={selectedPricingPlan}
            className={className}
            mutate={(data, onSuccess, onError) => mutate({ ...data, parkingId, id: planId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.copyEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.COPY}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.copyPricingPlanVersionSubtitle, {
                entry: selectedPricingPlan.translations.find((translation) => translation.language === locale)?.name,
            })}
        >
            {children}
        </PricingPlanFormModal>
    );
}

export default CopyPricingPlanVersionFormModal;
